import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { useTranslation, type TransKey } from '@root/shared/localization'

import { UserTradingAnalyticsPhishingSeverity } from '../../models/user-trading-analytics-phishing'

export type PhishingCheckMetricProps = {
  type: UserTradingAnalyticsPhishingSeverity
  titleKey: TransKey
  count: number
  percent: number
}

export const PhishingCheckMetric = ({
  type,
  titleKey,
  count,
  percent,
}: PhishingCheckMetricProps) => {
  const { t } = useTranslation()

  return (
    <Root>
      <Circle $color={COLOR_MAP[type]} />
      <Header $color={COLOR_MAP[type]}>{t(TYPE_TRANSFORM_MAP[type])}</Header>
      <Body>
        <Title>{t(titleKey)}</Title>
        <Value $color={VALUE_COLOR_MAP[type]}>
          <Numeric value={count} preset="full" />
          &nbsp;(
          <Numeric value={percent} preset="percents" />
          %)
        </Value>
      </Body>
    </Root>
  )
}

const COLOR_MAP: Record<UserTradingAnalyticsPhishingSeverity, string> = {
  [UserTradingAnalyticsPhishingSeverity.Normal]: 'var(--up-9)',
  [UserTradingAnalyticsPhishingSeverity.Warning]: 'var(--warning-9)',
  [UserTradingAnalyticsPhishingSeverity.Danger]: 'var(--down-9)',
}
const VALUE_COLOR_MAP: Record<UserTradingAnalyticsPhishingSeverity, string> = {
  [UserTradingAnalyticsPhishingSeverity.Normal]: 'var(--background-11)',
  [UserTradingAnalyticsPhishingSeverity.Warning]: 'var(--warning-9)',
  [UserTradingAnalyticsPhishingSeverity.Danger]: 'var(--down-9)',
}

const TYPE_TRANSFORM_MAP: Record<
  UserTradingAnalyticsPhishingSeverity,
  TransKey
> = {
  [UserTradingAnalyticsPhishingSeverity.Normal]:
    'profile.phishing-check.normal',
  [UserTradingAnalyticsPhishingSeverity.Warning]:
    'profile.phishing-check.warning',
  [UserTradingAnalyticsPhishingSeverity.Danger]:
    'profile.phishing-check.danger',
}

const Root = styled.div`
  border-radius: 10px;
  background: var(--appearanceA-5, var(--appearanceA-5));
  padding: 10px;
  position: relative;
`
const Header = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  position: relative;
`
const Body = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Title = styled.div`
  color: var(--text-10);

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
`
const Value = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
`

const Circle = styled.div<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  position: absolute;
  top: 6px;
  right: 6px;
`
